// Reusable components for the salon application
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  IconButton,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Close,
} from '@mui/icons-material';

// Export SearchableDropdown component
export { default as SearchableDropdown } from './SearchableDropdown';

// Export DataTable component
export { default as DataTable } from './DataTable';

// Export EmailTest component
export { default as EmailTest } from '../test/EmailTest';

// Stat Card Component
export const StatCard = ({
  title,
  value,
  icon,
  trend,
  trendValue,
  color = 'primary',
}) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp color="success" fontSize="small" />;
    if (trend === 'down') return <TrendingDown color="error" fontSize="small" />;
    return null;
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'success.main';
    if (trend === 'down') return 'error.main';
    return 'text.secondary';
  };

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={`${color}.main`}>
              {value}
            </Typography>
            {trendValue && (
              <Box display="flex" alignItems="center" mt={1}>
                {getTrendIcon()}
                <Typography variant="body2" color={getTrendColor()} ml={0.5}>
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}.main`,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Quick Action Card Component
export const QuickActionCard = ({
  title,
  description,
  icon,
  onClick,
  color = 'primary',
}) => {
  return (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
      onClick={onClick}
    >
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Client Avatar Component
export const ClientAvatar = ({
  firstName,
  lastName,
  size = 'medium',
}) => {
  const getInitials = () => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { width: 32, height: 32, fontSize: '0.875rem' };
      case 'large':
        return { width: 56, height: 56, fontSize: '1.25rem' };
      default:
        return { width: 40, height: 40, fontSize: '1rem' };
    }
  };

  return (
    <Avatar
      sx={{
        ...getSizeProps(),
        bgcolor: 'primary.main',
        color: 'white',
        fontWeight: 600,
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

// Status Chip Component
export const StatusChip = ({
  status,
  variant = 'filled',
}) => {
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
      case 'scheduled':
        return 'warning';
      case 'cancelled':
      case 'inactive':
        return 'error';
      case 'confirmed':
        return 'info';
      case 'in_progress':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Chip
      label={formatStatus(status)}
      color={getStatusColor(status)}
      variant={variant}
      size="small"
    />
  );
};

// Loading Component
export const Loading = ({ message = 'Loading...' }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="200px"
    >
      <CircularProgress size={48} />
      <Typography variant="body2" color="text.secondary" mt={2}>
        {message}
      </Typography>
    </Box>
  );
};

// Confirmation Dialog Component
export const ConfirmationDialog = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
}) => {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={onCancel} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          {cancelText}
        </Button>
        <Button onClick={onConfirm} color={confirmColor} variant="contained">
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Search Bar Component
export const SearchBar = ({
  value,
  onChange,
  placeholder = 'Search...',
  onClear,
}) => {
  return (
    <TextField
      fullWidth
      variant="outlined"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        endAdornment: value && onClear && (
          <IconButton onClick={onClear} size="small">
            <Close />
          </IconButton>
        ),
      }}
      sx={{ mb: 2 }}
    />
  );
};

// Error Message Component
export const ErrorMessage = ({ message, onClose }) => {
  return (
    <Snackbar 
      open={!!message} 
      autoHideDuration={6000} 
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert onClose={onClose} severity="error" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

// Success Message Component
export const SuccessMessage = ({ message, onClose }) => {
  return (
    <Snackbar 
      open={!!message} 
      autoHideDuration={4000} 
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert onClose={onClose} severity="success" sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
};

// Page Header Component
export const PageHeader = ({
  title,
  subtitle,
  action,
}) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={3}
      flexWrap="wrap"
      gap={2}
    >
      <Box>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body1" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
};
